import { useState } from 'react';
import * as React from 'react';
import { Link, LinkProps } from 'react-router-dom';
import 'twin.macro';
import 'styled-components/macro';

import { Button } from 'play/atoms/button/Button';
import { CarouselPagination } from 'play/molecules/carousel/CarouselPagination';
import { ChallengeNotes, Note } from 'play/organisms/notes-screen/ChallengeNotes';
import { Progress } from 'play/organisms/progress/Progress';
import { SplashLayout } from 'play/templates/splash-layout/SplashLayout';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';
import {
  ChallengeNoteType,
  Mode,
  NotesContainerInviteQuery,
  NotesContainerQuery,
  ProgressModalTrackFragment,
} from 'types/graphql';

import { useNotificationContext } from '../../../core/context/Notifications';
import { getAccessSetting } from '../../../support/accessSetting';
import { getShareURL } from '../../../support/invite';
import { ShareButton } from '../../molecules/share-button/ShareButton';

type NotesChallenge = NonNullable<NotesContainerQuery['challenge']>;
type NotesTrackChallenges = NonNullable<NotesContainerQuery['track']>['challenges'];

export interface NotesScreenProps {
  actions?: React.ReactNode;
  challenges: NotesTrackChallenges;
  currentChallenge: Pick<
    NotesChallenge,
    'id' | 'title' | 'permalink' | 'notes' | 'failed' | 'status' | 'slug' | 'type' | 'index'
  >;
  returnTo?: LinkProps['to'];
  title?: React.ReactNode;
  teamSlug: string;
  mode: Mode;
  track: ProgressModalTrackFragment;
  skippingEnabled: boolean;
  showShareButton?: boolean;
  invite?: NotesContainerInviteQuery['trackInvite'];
}

export const NotesScreen = ({
  actions,
  challenges = [],
  returnTo,
  title,
  currentChallenge,
  teamSlug,
  mode,
  track,
  skippingEnabled,
  showShareButton,
  invite,
}: NotesScreenProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const trackAmplitudeEvent = useTrackAmplitudeEvent();
  const { dispatch } = useNotificationContext();

  const { notes } = currentChallenge;

  const onClickBack = () => {
    const challengeNumber = challenges.findIndex(c => c.id === currentChallenge.id) + 1;
    const challengeCount = challenges.length;
    const challengesCompletedPct = Math.round((challengeNumber / challengeCount) * 10000) / 100;

    trackAmplitudeEvent({
      name: 'Lab - Challenge - Clicked back',
      data: {
        challengeNumber,
        challengeCount,
        challengesCompletedPct,
      },
    });
  };

  const onClickShare = async () => {
    try {
      if (!invite) throw new Error('No invite was found. Please try again later.');

      const inviteURL = getShareURL(teamSlug, invite.id);

      await navigator.clipboard.writeText(inviteURL);

      trackAmplitudeEvent({
        name: 'Lab - Clicked Share',
        data: {
          from: 'Notes screen',
          invite_access_setting: getAccessSetting({
            allowAnonymous: Boolean(invite.allowAnonymous),
            allowedEmailAddressesOnly: Boolean(invite.allowedEmailAddresses?.length),
          }),
        },
      });
    } catch (error) {
      dispatch('error', 'Failed to copy link', error instanceof Error ? error.message : '', 5);
    } finally {
      dispatch('success', 'Link copied', 'You can now share this collection of content.', 5);
    }
  };

  const filteredNotes = notes.reduce<Note[]>((results, note) => {
    if (note.type === ChallengeNoteType.Text) {
      if (note.contents) {
        results.push({
          id: note.id,
          type: note.type,
          contents: note.contents,
        });
      }
    } else if (note.url) {
      results.push({
        id: note.id,
        type: note.type,
        url: note.url,
      });
    }

    return results;
  }, []);

  return (
    <SplashLayout
      title={title}
      topRight={
        <span tw="flex">
          {showShareButton && <ShareButton onClick={onClickShare} tw="mr-1" />}
          <Progress
            teamSlug={teamSlug}
            challenges={challenges}
            currentChallenge={currentChallenge}
            mode={mode}
            track={track}
            skippingEnabled={skippingEnabled}
            tw="w-[125px]"
          />
        </span>
      }
      bottomLeft={
        returnTo ? (
          <Link to={returnTo}>
            <Button variant="outline" onClick={onClickBack}>
              Exit
            </Button>
          </Link>
        ) : (
          'Hands-On Lab'
        )
      }
      bottomCenter={
        <div tw="flex-grow min-w-0 mx-1">
          <CarouselPagination
            activeIndex={activeIndex}
            onActiveIndexChange={setActiveIndex}
            size={filteredNotes.length}
          />
        </div>
      }
      bottomRight={actions}
    >
      <ChallengeNotes
        tw="flex-grow"
        activeIndex={activeIndex}
        notes={filteredNotes}
        onActiveIndexChange={setActiveIndex}
      />
    </SplashLayout>
  );
};
